/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import memoize from "lodash.memoize";
import * as React from "react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {Body, Title} from "@khanacademy/wonder-blocks-typography";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedButton from "#components/button-package/deprecated-button.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedModal, {
    ModalFooter,
} from "#components/modal-package/deprecated-modal.tsx";

import * as sharedDiscussionStyles from "../shared-styles.ts";
import type {FeedbackType, FlagType, Post} from "../types.ts";

import FakeRadio from "./fake-radio.tsx";

type Props = {
    post: Post;
    tab: FeedbackType;
    onClose: (flagType?: FlagType | null | undefined) => void;
};

type State = {
    flagType: FlagType | null | undefined;
};

const StyledButton = addStyle("button");

type FlagString = {
    title: string;
    description: string;
};

const getFlagStrings = memoize(
    (): Partial<Record<FlagType, FlagString>> => ({
        inappropriate: {
            title: t`Flag as abuse`,
            description: t`disrespectful or offensive, an advertisement`,
        },
        lowquality: {
            title: t`Flag as not helpful`,
            description: t`Low quality, not about the video topic, soliciting votes or seeking badges, a homework question, repeatedly making the same post`,
        },
        shouldBeTipsOrThanks: {
            title: t`Flag as a tip or thanks`,
            description: t`Should be in “Tips & thanks”`,
        },
        shouldBeQuestion: {
            title: t`Flag as a question`,
            description: t`Should be in “Questions”`,
        },
        spam: {
            title: t`Flag as about the site`,
            description: t`A question about Khan Academy (e.g. “What software does Sal use?”), a post about badges, a technical problem with the site, a request for videos or features`,
        },
    }),
);

export default class DiscussionFlagModal extends React.Component<Props, State> {
    state: State = {
        flagType: null,
    };

    handleSubmit: () => void = () => {
        this.props.onClose(this.state.flagType);
    };

    render(): React.ReactNode {
        const {onClose, post, tab} = this.props;

        const flags = getFlagStrings();
        const flagTypes = ["inappropriate"];
        if (tab === "questions") {
            flagTypes.push("shouldBeTipsOrThanks");
        } else if (tab === "comments") {
            flagTypes.push("shouldBeQuestion");
        }
        flagTypes.push(...["lowquality", "spam"]);

        return (
            <DeprecatedModal style={styles.modal} onClose={onClose}>
                <View style={styles.content}>
                    <Title style={styles.title}>
                        {t`Flag for Guardian attention`}
                    </Title>
                    <View style={styles.post}>
                        <Body>{post.content}</Body>
                    </View>
                    <View>
                        {flagTypes.map((flagType, i) => (
                            <View
                                key={flagType}
                                style={[styles.row, i > 0 && styles.separator]}
                            >
                                <StyledButton
                                    style={styles.button}
                                    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type '"inappropriate" | "lowquality" | "spam" | "plagiarism" | "vote-soliciting" | "using-disallowed" | "shouldBeQuestion" | "shouldBeTipsOrThanks" | null | undefined'.
                                    onClick={() => this.setState({flagType})}
                                >
                                    <FakeRadio
                                        selected={
                                            this.state.flagType === flagType
                                        }
                                    />
                                    <Body>{flags[flagType].title}</Body>
                                </StyledButton>
                                <Body style={styles.desc}>
                                    {flags[flagType].description}
                                </Body>
                            </View>
                        ))}
                    </View>
                </View>
                <ModalFooter style={footerStyle}>
                    <DeprecatedButton
                        width={138}
                        color={color.blue}
                        onClick={this.handleSubmit}
                        disabled={!this.state.flagType}
                    >
                        {t`Submit`}
                    </DeprecatedButton>
                </ModalFooter>
            </DeprecatedModal>
        );
    }
}

const ROW_VERTICAL_PADDING = 16;
const RADIO_SIZE = 16;
const RADIO_MARGIN = 8;
const POST_PADDING = 16;
const FOOTER_PADDING = 16;

const styles = StyleSheet.create({
    modal: {
        width: 662,
        maxHeight: "80%",
    },
    title: {
        marginBottom: 24,
    },
    content: {
        flex: 1,
        padding: "64px 64px 0px 64px",
    },
    row: {
        padding: `${ROW_VERTICAL_PADDING}px 0`,
        flexShrink: 0,
        alignItems: "start",
    },
    button: {
        ...sharedDiscussionStyles.notAButton,
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",

        ":focus": {
            outline: "auto",
        },
    },
    separator: {
        borderTop: `solid 1px ${color.offBlack16}`,
    },
    desc: {
        color: color.offBlack64,
        paddingLeft: RADIO_SIZE + RADIO_MARGIN,
    },
    post: {
        flex: 1,
        border: `solid 1px ${color.offBlack16}`,
        color: color.offBlack64,
        borderRadius: 4,
        padding: POST_PADDING,
        overflow: "auto",
    },
});

// TODO(kevinb): update ModalFooter to accept an aphrodite style as well
const footerStyle = {
    justifyContent: "flex-end",
    paddingLeft: FOOTER_PADDING,
    paddingRight: FOOTER_PADDING,
} as const;
